export const CONTENT_SYNC_KEYS = Object.freeze({
  SITE_CONTENT: 'siteContentUpdatedAt',
  PRODUCTS: 'productsUpdatedAt',
  CERTIFICATES: 'certificatesUpdatedAt',
  NEWS: 'newsUpdatedAt',
  BANNERS: 'bannersUpdatedAt',
  CATALOGS: 'catalogsUpdatedAt',
  PRODUCT_CATEGORY_SLIDER: 'productCategorySliderUpdatedAt',
});

export const NO_CACHE_JSON_HEADERS = Object.freeze({
  Accept: 'application/json',
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  Pragma: 'no-cache',
  Expires: '0',
});

export function buildFreshUrl(url) {
  if (!url) {
    return url;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}t=${Date.now()}`;
}

export function subscribeToContentUpdates({
  keys = [],
  onUpdate,
  refreshOnFocus = true,
  refreshOnVisibilityChange = true,
  refreshOnPageShow = true,
  minIntervalMs = 15000,
}) {
  if (typeof window === 'undefined' || typeof onUpdate !== 'function') {
    return () => {};
  }

  const watchedKeys = new Set(keys.filter(Boolean));
  let refreshInFlight = null;
  let lastRefreshStartedAt = Date.now();

  const runRefresh = ({ force = false, reason = 'manual', key = null } = {}) => {
    if (refreshInFlight) {
      return refreshInFlight;
    }

    if (!force && minIntervalMs > 0 && Date.now() - lastRefreshStartedAt < minIntervalMs) {
      return Promise.resolve();
    }

    lastRefreshStartedAt = Date.now();

    refreshInFlight = Promise.resolve()
      .then(() => onUpdate({ reason, key }))
      .catch((error) => {
        console.error('Failed to refresh synced content:', error);
      })
      .finally(() => {
        refreshInFlight = null;
      });

    return refreshInFlight;
  };

  const shouldRefreshForKey = (key) =>
    watchedKeys.size === 0 || !key || watchedKeys.has(key);

  const handleStorage = (event) => {
    if (shouldRefreshForKey(event.key)) {
      runRefresh({ force: true, reason: 'storage', key: event.key });
    }
  };

  const handleVisibilityChange = () => {
    if (
      refreshOnVisibilityChange &&
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible'
    ) {
      runRefresh({ reason: 'visibilitychange' });
    }
  };

  const handlePageShow = (event) => {
    if (refreshOnPageShow && event.persisted) {
      runRefresh({ reason: 'pageshow' });
    }
  };

  if (refreshOnFocus) {
    window.addEventListener('focus', runRefresh);
  }

  if (refreshOnPageShow) {
    window.addEventListener('pageshow', handlePageShow);
  }

  window.addEventListener('storage', handleStorage);

  if (refreshOnVisibilityChange) {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  return () => {
    if (refreshOnFocus) {
      window.removeEventListener('focus', runRefresh);
    }

    if (refreshOnPageShow) {
      window.removeEventListener('pageshow', handlePageShow);
    }

    window.removeEventListener('storage', handleStorage);

    if (refreshOnVisibilityChange) {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
  };
}
